import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useConfigSitio } from '../hooks/useConfigSitio';

// Devuelve la localidad del corredor. Usa el campo dedicado si existe;
// si no (datos viejos), extrae la última parte del domicilio tras la última coma.
function localidadDe(c) {
  if (c.localidad) return c.localidad;
  if (!c.domicilio) return '';
  const partes = c.domicilio.split(',');
  return partes[partes.length - 1].trim();
}

export default function VenderPropiedad() {
  const config = useConfigSitio();
  const [corredores, setCorredores] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtroLocalidad, setFiltroLocalidad] = useState('Todas');

  useEffect(() => {
    async function cargar() {
      try {
        // vendedores es público; usuarios requiere auth. Leemos vendedores siempre
        // y usuarios como "mejor esfuerzo": si el visitante es anónimo y falla,
        // mostramos todos los vendedores igual (no rompemos la página pública).
        const vendedoresSnap = await getDocs(collection(db, 'vendedores'));
        let noCorredorIds = new Set();
        try {
          const usuariosSnap = await getDocs(collection(db, 'usuarios'));
          noCorredorIds = new Set(
            usuariosSnap.docs.filter(d => d.data().rol !== 'corredor').map(d => d.id)
          );
        } catch (e) { /* anónimo: sin acceso a usuarios, no filtramos por rol */ }
        const lista = vendedoresSnap.docs
          .map(d => ({ ...d.data(), id: d.id }))
          .filter(c => c.nombre && !noCorredorIds.has(c.id) && !c.dadaDeBaja && !c.ocultarEnPublico)
          .sort((a, b) => a.nombre.localeCompare(b.nombre));
        setCorredores(lista);
      } catch (e) {
        console.error(e);
      }
      setCargando(false);
    }
    cargar();
  }, []);

  // Localidades únicas presentes en la lista, ordenadas alfabéticamente
  const localidades = [
    'Todas',
    ...Array.from(new Set(corredores.map(c => localidadDe(c)).filter(Boolean))).sort(),
  ];

  const corredoresFiltrados = filtroLocalidad === 'Todas'
    ? corredores
    : corredores.filter(c => localidadDe(c) === filtroLocalidad);

  return (
    <div style={{ fontFamily: 'sans-serif' }}>

      {/* ── HERO ── */}
      <div style={{
        background: `linear-gradient(rgba(13,27,42,0.78), rgba(13,27,42,0.88)), url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1920&q=80') center/cover no-repeat`,
        padding: '64px 24px', textAlign: 'center', color: 'white',
      }}>
        <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#60a5fa', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '2px' }}>
          Vendé tu propiedad
        </p>
        <h1 style={{ margin: '0 0 16px', fontSize: '32px', fontWeight: '900', lineHeight: 1.2 }}>
          {config.venderTitulo}
        </h1>
        <p style={{ margin: '0 auto', maxWidth: '560px', color: '#cbd5e1', fontSize: '15px', lineHeight: 1.65 }}>
          {config.venderSubtitulo}
        </p>
      </div>

      {/* ── CÓMO FUNCIONA (callout destacado) ── */}
      <div style={{ background: '#1e3a5f', padding: '28px 24px' }}>
        <div style={{ maxWidth: '820px', margin: '0 auto', display: 'flex', alignItems: 'flex-start', gap: '18px' }}>
          <span style={{ fontSize: '38px', flexShrink: 0, lineHeight: 1 }}>💡</span>
          <div>
            <p style={{ color: '#93c5fd', fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1.5px', margin: '0 0 6px' }}>
              ¿Cómo publicar tu propiedad?
            </p>
            <p style={{ color: 'white', fontSize: '17px', fontWeight: '700', margin: '0 0 6px', lineHeight: 1.35 }}>
              Acercate a uno de los corredores de la CIF y pedile que te ayude a vender tu propiedad.
            </p>
            <p style={{ color: '#cbd5e1', fontSize: '14px', margin: 0, lineHeight: 1.7 }}>
              El corredor miembro de la CIF te acompañará durante todo el proceso de venta, tanto los aspectos legales
              como guiarte para ubicar el mejor precio de mercado. Elegí uno de la lista de abajo y contáctalo.
            </p>
          </div>
        </div>
      </div>

      {/* ── POR QUÉ UN CORREDOR CIF ── */}
      <div style={{ background: '#f8f9fa', padding: '52px 24px' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', margin: '0 0 10px', fontSize: '22px', color: '#1a1a1a' }}>
            ¿Por qué elegir un corredor de la CIF?
          </h2>
          <p style={{ textAlign: 'center', color: '#666', margin: '0 0 36px', fontSize: '14px' }}>
            Acercate a cualquiera de ellos: te asesoran sin compromiso.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
            {[
              ['🏛️', 'Habilitados legalmente', 'Habilitados para ejercer el corretaje inmobiliario y socios activos de la CIF.'],
              ['📋', 'Conocen el mercado local', 'Experiencia real en propiedades de la región y sus precios actuales.'],
              ['🔒', 'Seguridad jurídica', 'Te acompañan en toda la documentación y el proceso legal de la venta.'],
              ['📣', 'Mayor difusión', 'Tu propiedad aparece en FormoProp y otros canales de la red CIF.'],
            ].map(([emoji, titulo, desc]) => (
              <div key={titulo} style={{ background: 'white', borderRadius: '14px', padding: '26px 20px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', textAlign: 'center' }}>
                <span style={{ fontSize: '34px' }}>{emoji}</span>
                <h3 style={{ margin: '12px 0 8px', fontSize: '15px', color: '#1a1a1a' }}>{titulo}</h3>
                <p style={{ margin: 0, color: '#666', fontSize: '13px', lineHeight: 1.55 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── LISTADO DE CORREDORES ── */}
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '52px 24px' }}>
        <h2 style={{ textAlign: 'center', margin: '0 0 8px', fontSize: '22px', color: '#1a1a1a' }}>
          Corredores asociados a la CIF
        </h2>
        <p style={{ textAlign: 'center', color: '#666', margin: '0 0 28px', fontSize: '14px' }}>
          Elegí uno y contactalo para que te asesore sin compromiso.
        </p>

        {/* Filtro por localidad */}
        {!cargando && localidades.length > 2 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', marginBottom: '28px' }}>
            {localidades.map(loc => (
              <button
                key={loc}
                onClick={() => setFiltroLocalidad(loc)}
                style={{
                  padding: '7px 18px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                  border: filtroLocalidad === loc ? 'none' : '1.5px solid #d1d5db',
                  background: filtroLocalidad === loc ? '#1e3a5f' : 'white',
                  color: filtroLocalidad === loc ? 'white' : '#374151',
                  transition: 'all 0.15s',
                }}
              >
                {loc === 'Todas' ? '📍 Todas las localidades' : `📍 ${loc}`}
                {loc !== 'Todas' && (
                  <span style={{ marginLeft: '6px', opacity: 0.75, fontWeight: 'normal' }}>
                    ({corredores.filter(c => localidadDe(c) === loc).length})
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {cargando ? (
          <div style={{ textAlign: 'center', color: '#aaa', padding: '48px' }}>Cargando corredores...</div>
        ) : corredores.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#aaa', padding: '48px' }}>
            <p>No hay corredores registrados aún.</p>
            <a href="https://cif.org.ar/#miembros" target="_blank" rel="noreferrer"
              style={{ color: '#007bff', fontWeight: 'bold' }}>Ver directorio oficial CIF</a>
          </div>
        ) : corredoresFiltrados.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#aaa', padding: '48px' }}>
            No hay corredores registrados en {filtroLocalidad}.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '18px' }}>
            {corredoresFiltrados.map(c => {
              const ciudad = localidadDe(c);
              return (
                <Link to={c.slug ? `/${c.slug}` : `/corredor/${c.id}`} key={c.id} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div
                    style={{
                      background: 'white', borderRadius: '14px', padding: '20px 22px',
                      boxShadow: '0 3px 12px rgba(0,0,0,0.07)',
                      display: 'flex', alignItems: 'center', gap: '16px',
                      transition: 'box-shadow 0.2s, transform 0.2s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.13)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 3px 12px rgba(0,0,0,0.07)'; e.currentTarget.style.transform = 'none'; }}
                  >
                    {/* Avatar */}
                    {c.fotoPerfilUrl
                      ? <img src={c.fotoPerfilUrl} style={{ width: '62px', height: '62px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '2px solid #f0f0f0' }} alt="" />
                      : c.logoUrl
                        ? <img src={c.logoUrl} style={{ width: '62px', height: '62px', objectFit: 'contain', borderRadius: '10px', flexShrink: 0, border: '1px solid #eee' }} alt="" />
                        : <div style={{ width: '62px', height: '62px', borderRadius: '50%', background: '#e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px', flexShrink: 0 }}>👤</div>
                    }

                    {/* Info */}
                    <div style={{ minWidth: 0 }}>
                      <p style={{ margin: '0 0 4px', fontWeight: '700', fontSize: '15px', color: '#1a1a1a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {c.nombre}
                      </p>

                      {/* Badge de localidad */}
                      {ciudad && (
                        <span style={{
                          display: 'inline-block', marginBottom: '6px',
                          padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
                          background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe',
                        }}>
                          📍 {ciudad}
                        </span>
                      )}

                      {c.domicilio && (
                        <p style={{ margin: '0 0 6px', fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {c.domicilio}
                        </p>
                      )}
                      {c.whatsapp ? (
                        <a
                          href={`https://wa.me/${c.whatsapp}`}
                          onClick={e => e.stopPropagation()}
                          target="_blank" rel="noreferrer"
                          style={{ fontSize: '12px', color: '#25d366', fontWeight: '700', textDecoration: 'none' }}
                        >
                          WhatsApp →
                        </a>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#007bff', fontWeight: '600' }}>Ver perfil →</span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* CTA final */}
        <div style={{ textAlign: 'center', marginTop: '48px', padding: '32px', background: '#f0f4ff', borderRadius: '16px', border: '1px solid #dbe8ff' }}>
          <p style={{ margin: '0 0 6px', fontWeight: '700', fontSize: '17px', color: '#1a1a1a' }}>¿Querés ver el directorio oficial completo?</p>
          <p style={{ margin: '0 0 20px', color: '#666', fontSize: '14px' }}>
            En el sitio de la CIF encontrás el directorio completo de corredores habilitados y socios de Formosa.
          </p>
          <a
            href="https://cif.org.ar/#miembros"
            target="_blank" rel="noreferrer"
            style={{ display: 'inline-block', padding: '12px 28px', background: '#1a1a1a', color: 'white', borderRadius: '8px', textDecoration: 'none', fontWeight: 'bold', fontSize: '14px' }}
          >
            Directorio oficial CIF
          </a>
        </div>
      </div>

      {/* Volver */}
      <div style={{ textAlign: 'center', paddingBottom: '40px' }}>
        <Link to="/" style={{ color: '#007bff', fontSize: '14px', textDecoration: 'none' }}>← Volver al catálogo</Link>
      </div>
    </div>
  );
}
